/**
 * ConfiguratorShared
 * Single source of truth for shared configurator logic (DRY):
 * - number normalization
 * - slug/model canonicalization for zip-code-validator app proxy endpoints
 * - abortable, debounced JSON POST client for installation-cost API
 */
(function () {
  const root = window;

  const normalizeNumber = (value, fallback = 0) => {
    const n = typeof value === 'string' ? Number(value) : value;
    return Number.isFinite(n) ? n : fallback;
  };

  const formatUSD = (dollars, { minDecimals = 0, maxDecimals = 0 } = {}) => {
    const n = normalizeNumber(dollars, 0);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: minDecimals,
      maximumFractionDigits: maxDecimals,
    }).format(n);
  };

  const formatPriceSmart = (dollars) => {
    const n = normalizeNumber(dollars, 0);
    const hasCents = Math.abs(n % 1) > 0.000001;
    return formatUSD(n, { minDecimals: hasCents ? 2 : 0, maxDecimals: hasCents ? 2 : 0 });
  };

  /**
   * Prefer theme currency formatter if present; fallback to Intl.
   * @param {number} dollars
   * @param {{ noDecimals?: boolean }} opts
   */
  const formatMoneyFromDollars = (dollars, opts = {}) => {
    const cents = Math.round(normalizeNumber(dollars, 0) * 100);
    const noDecimals = Boolean(opts.noDecimals);
    if (root.Utility?.formatMoney) {
      const fmt = noDecimals ? '${{ amount_no_decimals }}' : '${{ amount }}';
      return root.Utility.formatMoney(cents, fmt);
    }
    return formatUSD(cents / 100, { minDecimals: noDecimals ? 0 : 2, maxDecimals: noDecimals ? 0 : 2 });
  };

  const slugify = (value) => String(value ?? '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-');

  const canonicalModel = (rawModel) => {
    const slug = slugify(rawModel);

    if (slug.startsWith('aspect')) return 'aspect';
    if (slug.startsWith('signature')) return 'signature';
    if (slug.startsWith('portland')) return 'portland';
    if (slug.startsWith('spruce')) return 'spruce';
    if (slug.startsWith('carriage')) return 'carriage-house';

    // Handle Summit models: transform to API format
    // Examples:
    //   summit-800-model-a → summit-800-a
    //   summit-800-a → summit-800-a (already correct)
    //   summit-680-model-c → summit-680-c
    if (slug.startsWith('summit-')) {
      // Match pattern: summit-{size}-{anything}-{letter} or summit-{size}-{letter}
      // Extract summit-{size} and the final letter variant
      const match = slug.match(/^(summit-\d+)(?:-.*)?-([a-z])$/);
      if (match) {
        return `${match[1]}-${match[2]}`; // e.g., summit-800-a
      }
      // If no letter variant, just return summit-{size}
      const sizeMatch = slug.match(/^(summit-\d+)/);
      if (sizeMatch) {
        return sizeMatch[1]; // e.g., summit-800
      }
    }
    
    if (slug.startsWith('summit')) return 'summit';

    return slug;
  };

  const pickFirst = (obj, keys) => {
    for (const k of keys) {
      const v = obj?.[k];
      if (v != null && String(v).trim() !== '') return v;
    }
    return '';
  };

  const buildInstallationCostPayload = ({ zip, model, depth, length, interior, kitchen, roofStyle, base_product_price }) => {
    // Helper to normalize option values to kebab-case (lowercase with hyphens)
    // Converts "Lean To" -> "lean-to", "Shell Only" -> "shell-only", etc.
    const normalizeOption = (val) => val ? slugify(val) : undefined;
    
    const payload = {
      zip: String(zip || '').trim(),
      model: canonicalModel(model),
      depth: depth == null ? undefined : normalizeNumber(depth, undefined),
      length: length == null ? undefined : normalizeNumber(length, undefined),
      interior: normalizeOption(interior),
      kitchen: normalizeOption(kitchen),
      roofStyle: normalizeOption(roofStyle),
    };
    
    // Add base_product_price for all models (required by final-price API)
    if (base_product_price != null) {
      payload.base_product_price = normalizeNumber(base_product_price, undefined);
    }
    
    return payload;
  };

  /**
   * Map "installation option value" -> API cost keys.
   * Examples:
   * - "product-only" => [] (no installation, returns empty - cost should be 0)
   * - "shell-only" => ["shell"]
   * - "shell-lifestyle" => ["shell", "lifestyle"]  (total is sum)
   * - "shell-kitchenbath" => ["shell", "kitchenbath"] (total is sum)
   */
  const getInstallationCostKeys = (value) => {
    const v = String(value || '').toLowerCase();
    const keys = [];

    // If lifestyle or kitchenbath is included, their breakdown cost ALREADY includes the base shell install.
    // So we should only push 'shell' if it is a purely shell-only option.
    if (v.includes('lifestyle')) {
      keys.push('lifestyle');
    } else if (v.includes('kitchenbath')) {
      keys.push('kitchenbath');
    } else if (v.includes('shell')) {
      keys.push('shell');
    }

    return keys;
  };

  /**
   * Check if an installation option is a "product only" type (no installation service).
   * These options should have $0 installation cost.
   */
  const isProductOnlyOption = (value) => {
    const v = String(value || '').toLowerCase();
    // "product-only", "product_only", "productonly", "product" without other keywords
    // Also covers: "none", "no-installation", "diy"
    return (
      v.includes('product') && !v.includes('shell') && !v.includes('lifestyle') && !v.includes('kitchenbath')
    ) || v === 'none' || v === 'diy' || v.includes('no-install');
  };

  /**
   * Compute the installation total for the selected option. Some options are additive:
   * "shell + lifestyle" == shell + lifestyle.
   * 
   * IMPORTANT: "product-only" and similar options return 0 (no installation fee).
   * Only options containing "shell", "lifestyle", or "kitchenbath" incur installation costs.
   */
  const computeInstallationCost = (data, selectedType) => {
    if (!data) return 0;
    
    // Product-only options have no installation cost
    if (isProductOnlyOption(selectedType)) {
      return 0;
    }
    
    const keys = getInstallationCostKeys(selectedType);
    
    // If no valid keys found and not product-only, return 0 (safe default)
    // Do NOT default to 'shell' as that would incorrectly charge for installation
    if (!keys.length) {
      return 0;
    }
    
    return keys.reduce((sum, k) => sum + normalizeNumber(data?.[k], 0), 0);
  };

  const selectInstallationCost = (data, selectedType) => {
    // Back-compat alias (older code expects this name)
    return computeInstallationCost(data, selectedType);
  };

  const createInstallationCostClient = ({
    endpointPath = '/apps/zip-code-validator/api/final-price',
    timeoutMs = 15000,
    debounceMs = 250,
  } = {}) => {
    let abortController = null;
    let timer = null;
    let lastPayloadKey = null;
    let lastRaw = null;

    // Handle both absolute and relative URLs correctly
    let endpointUrl;
    try {
      // If endpointPath is already absolute, use it directly
      if (endpointPath.startsWith('http://') || endpointPath.startsWith('https://')) {
        endpointUrl = endpointPath;
      } else {
        // For relative paths, construct with base URL
        endpointUrl = new URL(endpointPath, window.location.origin).toString();
      }
    } catch (e) {
      console.error('Invalid endpoint URL:', endpointPath, e);
      endpointUrl = endpointPath; // Fallback to original
    }

    const fetchInstallationCost = async (payload) => {
      if (abortController) {
        abortController.abort();
      }
      abortController = new AbortController();

      const timeoutId = setTimeout(() => {
        if (abortController) {
          abortController.abort();
        }
      }, timeoutMs);
      
      try {
        const res = await fetch(endpointUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: abortController.signal,
        });

        if (!res.ok) {
          throw new Error(`Installation cost request failed (${res.status})`);
        }
        
        const json = await res.json();
        // Debug: print raw installation API response in the browser console.
        // eslint-disable-next-line no-console
        console.log('[final-price api] response:', json.data);
        const ok = Boolean(json?.success ?? json?.status);
        if (!ok) {
          throw new Error(json?.message || 'Installation cost request failed');
        }
        return json?.data || null;
      } catch (error) {
        // Handle abort errors gracefully
        if (error.name === 'AbortError') {
          throw new Error('Installation cost request timed out or was cancelled');
        }
        throw error;
      } finally {
        clearTimeout(timeoutId);
        abortController = null;
      }
    };

    /**
     * Debounced fetch with simple de-dupe keying.
     * @returns Promise<{ installation: { ...raw, selected_type, selected_cost }, raw }>
     */
    const queue = ({ payload, selectedType }) => new Promise((resolve, reject) => {
      const payloadKey = JSON.stringify(payload);
      const selected = String(selectedType || 'shell');

      // If the payload hasn't changed and we already have raw data, return immediately with
      // a recomputed selected_cost (no network).
      if (payloadKey === lastPayloadKey && lastRaw) {
        const selected_cost = computeInstallationCost(lastRaw, selected);
        resolve({
          raw: lastRaw,
          meta: { didFetch: false, fromCache: true, payloadKey },
          installation: { ...lastRaw, selected_type: selected, selected_cost },
        });
        return;
      }

      lastPayloadKey = payloadKey;

      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      
      timer = setTimeout(async () => {
        timer = null; // Clear timer reference when executing
        try {
          const raw = await fetchInstallationCost(payload);
          lastRaw = raw;
          const selected_cost = computeInstallationCost(raw, selected);
          resolve({
            raw,
            meta: { didFetch: true, fromCache: false, payloadKey },
            installation: raw ? { ...raw, selected_type: selected, selected_cost } : null,
          });
        } catch (e) {
          reject(e);
        }
      }, debounceMs);
    });

    return { queue };
  };

  /**
   * Shared glue: given a configurator-like context and a client, build payload, queue request,
   * and return normalized structures usable by all modals.
   *
   * @param {{
   *  client: { queue: Function },
   *  zip: string,
   *  modelRaw: string,
   *  depth?: number,
   *  length?: number,
   *  currentConfig?: any,
   *  selectedType?: string,
   * }} args
   */
  const queueInstallationPricing = async ({
    client,
    zip,
    modelRaw,
    depth,
    length,
    currentConfig,
    selectedType,
    base_product_price,
  }) => {
    if (!client?.queue) throw new Error('Missing installation cost client');

    const interior = pickFirst(currentConfig, ['interior', 'interiorFinish', 'mainFloorFinish', 'flooring']);
    const kitchen = pickFirst(currentConfig, ['kitchen']);
    const roofStyle = pickFirst(currentConfig, ['roofStyle', 'roof', 'roofType']);

    const payload = buildInstallationCostPayload({
      zip,
      model: modelRaw,
      depth,
      length,
      interior: interior || undefined,
      kitchen: kitchen || undefined,
      roofStyle: roofStyle || undefined,
      base_product_price,
    });

    const result = await client.queue({ payload, selectedType });
    if (!result) return null;

    // Extract data from new API response format
    // API returns: { product_price, shipping_cost, installation_cost, total_price, breakdown: { product, shipping, installation } }
    const raw = result.raw || {};
    const breakdownInstallation = raw.breakdown?.installation || {};
    const breakdownShipping = raw.breakdown?.shipping || {};
    
    // Build installation object with prices from breakdown
    const installationWithPrices = {
      shell: normalizeNumber(breakdownInstallation.shell, 0),
      lifestyle: normalizeNumber(breakdownInstallation.lifestyle, 0),
      kitchenbath: normalizeNumber(breakdownInstallation.kitchenbath, 0),
      selected_type: breakdownInstallation.selected_type || selectedType,
      selected_cost: normalizeNumber(breakdownInstallation.selected_cost || raw.installation_cost, 0),
      state_code: breakdownInstallation.state_code,
      zone: breakdownInstallation.zone,
    };

    const finalPriceData = {
      breakdown: { 
        installation: installationWithPrices,
        // Store individual prices for easy access in services tab
        installationPrices: {
          shell: normalizeNumber(breakdownInstallation.shell, 0),
          lifestyle: normalizeNumber(breakdownInstallation.lifestyle, 0),
          kitchenbath: normalizeNumber(breakdownInstallation.kitchenbath, 0),
        },
        // Store shipping breakdown for detailed display
        shipping: {
          base_subtotal: normalizeNumber(breakdownShipping.base_subtotal, 0),
          depth_surcharge: normalizeNumber(breakdownShipping.depth_surcharge, 0),
          interior_surcharge: normalizeNumber(breakdownShipping.interior_surcharge, 0),
          inflation_surcharge: normalizeNumber(breakdownShipping.inflation_surcharge, 0),
        },
        // Store product breakdown
        product: {
          base_price: normalizeNumber(raw.breakdown?.product?.base_price || raw.product_price, 0),
        },
      },
      installation_cost: normalizeNumber(raw.installation_cost || installationWithPrices.selected_cost, 0),
      // Include shipping cost from API response (new format uses shipping_cost at top level)
      shipping_cost: normalizeNumber(raw.shipping_cost, 0),
      // Include total price from API
      total_price: normalizeNumber(raw.total_price, 0),
      product_price: normalizeNumber(raw.product_price, 0),
    };

    return { 
      payload, 
      raw: result.raw, 
      installation: installationWithPrices, 
      finalPriceData, 
      meta: result.meta || null 
    };
  };

  const applyInstallationCostsToServiceOptions = ({
    container,
    installation,
    optionValues = null,
    cardSelector = '[data-service-option]',
    valueDatasetKey = 'value',
    priceSelector = '[data-service-price]',
    formatPrice = formatMoneyFromDollars,
    prefixPlus = false,
  }) => {
    if (!container || !installation) return;

    const ensurePriceEl = (card) => {
      const existing = card.querySelector(priceSelector);
      if (existing) return existing;

      // Create a placeholder price element so it can be updated dynamically after the ZIP API returns.
      const header =
        card.querySelector('[data-service-header]') ||
        card.querySelector('.service-option-header') ||
        card;

      const span = document.createElement('span');
      span.className = 'service-price';
      span.setAttribute('data-service-price', '');

      const checkmark = header.querySelector('[data-service-checkmark]') || header.querySelector('.service-checkmark');
      if (checkmark && checkmark.parentNode === header) {
        header.insertBefore(span, checkmark);
      } else {
        header.appendChild(span);
      }
      return span;
    };

    const updateCard = (card, value) => {
      const keys = getInstallationCostKeys(value);
      if (!keys.length) return;
      
      // For lifestyle option (value contains "lifestyle"), show only lifestyle price, not shell + lifestyle
      const valueLower = String(value || '').toLowerCase().trim();
      const containsLifestyle = valueLower.includes('lifestyle');
      const keysToUse = containsLifestyle ? ['lifestyle'] : keys;
      
      const cost = keysToUse.reduce((sum, k) => sum + normalizeNumber(installation?.[k], 0), 0);

      card.dataset.price = String(cost);
      const priceEl = ensurePriceEl(card);
      const formatted = formatPrice(cost, { noDecimals: true });
      priceEl.textContent = prefixPlus ? `+${formatted}` : formatted;
    };

    // If explicit option values were provided, update those; otherwise, discover from DOM.
    if (Array.isArray(optionValues) && optionValues.length) {
      for (const value of optionValues) {
        const card = container.querySelector(`${cardSelector}[data-${valueDatasetKey}="${CSS.escape(value)}"]`);
        if (!card) continue;
        updateCard(card, value);
      }
      return;
    }

    container.querySelectorAll(cardSelector).forEach((card) => {
      const value = card?.dataset?.[valueDatasetKey];
      if (!value) return;
      updateCard(card, value);
    });
  };

  /**
   * Robust notification helper - ensures errors/success messages are always shown to users.
   * Falls back gracefully if notification element is not available.
   * @param {'error' | 'success'} type - Notification type
   * @param {string} message - The message to display
   * @param {string} [title] - Optional title (defaults based on type)
   */
  const showNotification = (type, message, title) => {
    const defaultTitle = type === 'error' ? 'Error' : 'Success';
    const displayTitle = title || defaultTitle;
    const displayMessage = typeof message === 'string' ? message : String(message || 'An error occurred');

    // Try using the existing Utility helper first
    if (type === 'error' && typeof root.Utility?.handleError === 'function') {
      try {
        root.Utility.handleError(displayMessage, displayTitle);
        return;
      } catch (e) {
        // Fall through to fallback
      }
    }

    if (type === 'success' && typeof root.Utility?.handleSuccess === 'function') {
      try {
        root.Utility.handleSuccess(displayMessage, displayTitle);
        return;
      } catch (e) {
        // Fall through to fallback
      }
    }

    // Fallback: try notification element directly
    if (root.notificationEle && typeof root.notificationEle.updateNotification === 'function') {
      try {
        root.notificationEle.updateNotification(displayTitle, displayMessage, {
          type: type,
          timeout: type === 'error' ? 10000 : 5000
        });
        return;
      } catch (e) {
        // Fall through to console
      }
    }

    // Final fallback: console output (never silently fail)
    if (type === 'error') {
      console.error(`[${displayTitle}]`, displayMessage);
    } else {
      console.info(`[${displayTitle}]`, displayMessage);
    }
  };

  /**
   * Show an error notification to the user
   * @param {string} message - Error message
   * @param {string} [title] - Optional title
   */
  const showError = (message, title) => showNotification('error', message, title);

  /**
   * Show a success notification to the user
   * @param {string} message - Success message
   * @param {string} [title] - Optional title
   */
  const showSuccess = (message, title) => showNotification('success', message, title);

  /**
   * Show contact link on API error
   * @param {HTMLElement} container - The container element (form or step2Section)
   * @param {string} [contactUrl='/pages/contact-us'] - The contact page URL
   */
  const showContactLinkOnError = (container, contactUrl = '/pages/contact-us') => {
    if (!container) return;
    
    // Remove any existing contact link
    const existingLink = container.querySelector('[data-contact-error-link]');
    if (existingLink) existingLink.remove();
    
    // Also check parent for existing link
    const parentExisting = container.parentElement?.querySelector('[data-contact-error-link]');
    if (parentExisting) parentExisting.remove();

    // Find the footer with Save Design button
    const footer = container.closest('[data-panel-content-wrapper]')?.parentElement?.querySelector('[data-panel-footer]') || 
                   container.querySelector('[data-panel-footer]') ||
                   container.closest('[data-configurator-step="2"]')?.querySelector('[data-panel-footer]');
    
    if (footer) {
      // Remove any existing contact link in footer
      const footerExisting = footer.querySelector('[data-contact-error-link]');
      if (footerExisting) footerExisting.remove();
      
      const contactLink = document.createElement('div');
      contactLink.setAttribute('data-contact-error-link', '');
      contactLink.className = 'text-center mt-3';
      contactLink.innerHTML = `
        <p class="mb-0 fs-sm" style="color: #666;">
          Having trouble? <a href="${contactUrl}" class="text-primary fw-medium" style="text-decoration: underline;">Contact us</a> for assistance.
        </p>
      `;
      footer.appendChild(contactLink);
    }
  };

  /**
   * Validates a ZIP code by making an immediate (non-debounced) API call.
   * This is used to gate step progression - the step should NOT advance until this returns successfully.
   * 
   * @param {{
   *   zip: string,
   *   model: string,
   *   depth?: number,
   *   length?: number,
   *   interior?: string,
   *   kitchen?: string,
   *   roofStyle?: string,
   *   endpointPath?: string,
   *   timeoutMs?: number,
   * }} params
   * @returns {Promise<{ data: object, raw: object }>} - Resolves with API data on success
   * @throws {Error} - Rejects with error on failure (non-200, validation error, timeout, etc.)
   */
  const validateZipCodeWithAPI = async ({
    zip,
    model,
    depth,
    length,
    interior,
    kitchen,
    roofStyle,
    base_product_price,
    endpointPath = '/apps/zip-code-validator/api/final-price',
    timeoutMs = 15000,
  }) => {
    const zipTrimmed = String(zip || '').trim();
    
    // Basic client-side validation first
    if (!zipTrimmed) {
      throw new Error('Please enter a valid ZIP code.');
    }
    
    // US ZIP code format validation (5 digits or 5+4 format)
    const zipPattern = /^\d{5}(-\d{4})?$/;
    if (!zipPattern.test(zipTrimmed)) {
      throw new Error('Please enter a valid 5-digit ZIP code.');
    }

    // DUPLICATE CALL PROTECTION: Check if this exact request is already in progress
    if (!window._zipValidationCalls) {
      window._zipValidationCalls = new Map();
    }
    
    // Create a unique key for this request
    const requestKey = `${zipTrimmed}_${model}_${depth}_${length}_${base_product_price}`;
    const existingCall = window._zipValidationCalls.get(requestKey);
    const now = Date.now();
    
    // If a request with the same parameters is already in progress (within 3 seconds), return it
    if (existingCall && (now - existingCall.timestamp) < 3000) {
      console.log('[ZIP Validation] Returning cached promise for duplicate call', { requestKey });
      return existingCall.promise;
    }

    const payload = buildInstallationCostPayload({
      zip: zipTrimmed,
      model,
      depth,
      length,
      interior,
      kitchen,
      roofStyle,
      base_product_price,
    });

    // Handle URL construction
    let endpointUrl;
    try {
      if (endpointPath.startsWith('http://') || endpointPath.startsWith('https://')) {
        endpointUrl = endpointPath;
      } else {
        endpointUrl = new URL(endpointPath, root.location.origin).toString();
      }
    } catch (e) {
      endpointUrl = endpointPath;
    }

    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), timeoutMs);

    // Create the API promise and store it to prevent duplicate calls
    const apiPromise = (async () => {
      try {
        console.log('[ZIP Validation] Making API request', { requestKey, endpoint: endpointUrl });
        
        const res = await fetch(endpointUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: abortController.signal,
        });

        // CRITICAL: Check for non-200 response status
        if (!res.ok) {
          if (res.status === 400) {
            throw new Error('Invalid ZIP code. Please check and try again.');
          } else if (res.status === 404) {
            throw new Error('Service not available for this ZIP code.');
          } else if (res.status >= 500) {
            throw new Error('Service temporarily unavailable. Please try again.');
          }
          throw new Error(`Validation failed (${res.status}). Please try again.`);
        }

        const json = await res.json();
        
        // Check for API-level success/failure
        const isSuccess = Boolean(json?.success ?? json?.status);
        if (!isSuccess) {
          const apiMessage = json?.message || json?.error || 'ZIP code validation failed.';
          throw new Error(apiMessage);
        }

        // Check if data is present
        const data = json?.data;
        if (!data) {
          throw new Error('No pricing data available for this ZIP code.');
        }

        return { data, raw: json, payload };
      } catch (error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timed out. Please check your connection and try again.');
        }
        // Re-throw with user-friendly message if it's a network error
        if (error.message === 'Failed to fetch' || error.message.includes('NetworkError')) {
          throw new Error('Network error. Please check your connection and try again.');
        }
        throw error;
      } finally {
        clearTimeout(timeoutId);
        // Remove from cache when done (success or error)
        window._zipValidationCalls.delete(requestKey);
      }
    })();

    // Store the promise immediately to prevent duplicate calls
    window._zipValidationCalls.set(requestKey, { promise: apiPromise, timestamp: now });
    
    // Clean up old entries (older than 10 seconds)
    for (const [key, value] of window._zipValidationCalls.entries()) {
      if (now - value.timestamp > 10000) {
        window._zipValidationCalls.delete(key);
      }
    }

    return apiPromise;
  };

  /**
   * Format phone number to 123-456-7890 format
   */
  const formatPhoneNumber = (phone) => {
    if (!phone) return undefined;
    // Remove all non-digits
    const digits = String(phone).replace(/\D/g, '');
    // Remove leading 1 if present
    const cleaned = digits.startsWith('1') && digits.length === 11 ? digits.slice(1) : digits;
    // Format to 123-456-7890
    if (cleaned.length === 10) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone; // Return as-is if can't format
  };

  /**
   * Convert kebab-case to Title Case
   * Example: 'iron-gray' -> 'Iron Gray'
   */
  const kebabToTitleCase = (str) => {
    if (!str) return '';
    return String(str)
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  /**
   * Build customerData payload for Save Design API
   */
  const buildCustomerData = (formData) => {
    const phone = formatPhoneNumber(formData.phone);
    return {
      first_name: String(formData.firstName || '').trim(),
      last_name: String(formData.lastName || '').trim(),
      email: String(formData.email || '').trim(),
      zip: String(formData.zipCode || '').trim(),
      ...(phone && { phone }),
      is_demo: false
    };
  };

  /**
   * Build designData payload for Save Design API
   * 
   * Required fields by model type:
   * - Aspect: orientation, interiorKit, accentColor, layout, kitchen, exterior, roof, mainFloorFinish, bathFloorFinish, fixtureFinish, counterFinish, cabinetFinish
   * - Summit: orientation, interiorKit, summitCode, summitOption1, summitOption2, summitOption3, mainFloorFinish, bathFloorFinish, fixtureFinish, counterFinish, cabinetFinish
   * - Signature/Portland: interiorKit, mainFloorFinish, frontElevation, backElevation, leftElevation, rightElevation, exteriorAddon, roofAddon
   */
  const buildDesignData = (configurator) => {
    const modelSlug = configurator.getModelSlug?.() || configurator.modelConfig?.name || 'summit';
    const modelsData = configurator.modelsData || {};
    const currentConfig = configurator.currentConfig || {};
    
    const designData = {
      model: modelSlug,
      size: modelsData.size || configurator.modelConfig?.size,
      area: normalizeNumber(modelsData.squareFootage || configurator.modelConfig?.squareFootage, undefined),
      length: normalizeNumber(modelsData.length || configurator.modelConfig?.length, undefined),
      depth: normalizeNumber(modelsData.depth || configurator.modelConfig?.depth, undefined),
    };

    // ============================================================================
    // COMMON FIELDS (all models)
    // ============================================================================
    
    // Orientation (L/R)
    if (currentConfig.orientation) {
      // Normalize to "L" or "R" for API
      const orientVal = String(currentConfig.orientation).toLowerCase();
      designData.orientation = orientVal.startsWith('l') ? 'L' : 'R';
    }
    
    // Interior Kit (lifestyle/shell-only)
    if (currentConfig.interiorOptions || currentConfig.interiorPackage || currentConfig.interior) {
      designData.interiorKit = currentConfig.interiorOptions || currentConfig.interiorPackage || currentConfig.interior;
    }
    
    // Siding
    if (currentConfig.siding) designData.siding = currentConfig.siding;
    
    // Handle sidingColor - can be string or object with main/accent properties
    if (currentConfig.sidingColor) {
      if (typeof currentConfig.sidingColor === 'object' && currentConfig.sidingColor !== null) {
        if (currentConfig.sidingColor.accent) {
          designData.sidingColor = currentConfig.sidingColor.main || currentConfig.sidingColor;
          designData.accentColor = currentConfig.sidingColor.accent;
        } else if (currentConfig.sidingColor.main) {
          designData.sidingColor = currentConfig.sidingColor.main;
        }
      } else {
        designData.sidingColor = currentConfig.sidingColor;
      }
    }
    
    // Add accentColor if it exists separately
    if (currentConfig.accentColor) designData.accentColor = currentConfig.accentColor;
    
    // Other color fields
    if (currentConfig.trimColor) designData.trimColor = currentConfig.trimColor;
    if (currentConfig.doorColor) designData.doorColor = currentConfig.doorColor;
    if (currentConfig.fasciaColor) designData.fasciaColor = currentConfig.fasciaColor;
    if (currentConfig.soffitColor) designData.soffitColor = currentConfig.soffitColor;
    if (currentConfig.eavesColor) designData.eavesColor = currentConfig.eavesColor;
    if (currentConfig.windowCasement) designData.windowCasement = currentConfig.windowCasement;
    
    // Interior finish options (shared by Aspect, Summit, Signature, Portland when Lifestyle selected)
    if (currentConfig.mainFloorFinish || currentConfig.flooring) {
      designData.mainFloorFinish = currentConfig.mainFloorFinish || currentConfig.flooring;
    }
    if (currentConfig.bathFloorFinish) designData.bathFloorFinish = currentConfig.bathFloorFinish;
    if (currentConfig.fixtureFinish || currentConfig.fixture) {
      designData.fixtureFinish = currentConfig.fixtureFinish || currentConfig.fixture;
    }
    if (currentConfig.counterFinish || currentConfig.countertop) {
      designData.counterFinish = currentConfig.counterFinish || currentConfig.countertop;
    }
    if (currentConfig.cabinetFinish || currentConfig.cabinets) {
      designData.cabinetFinish = currentConfig.cabinetFinish || currentConfig.cabinets;
    }

    // ============================================================================
    // ASPECT-SPECIFIC FIELDS
    // ============================================================================
    if (modelSlug.startsWith('aspect') || modelSlug === 'aspect') {
      if (currentConfig.layout) designData.layout = currentConfig.layout;
      if (currentConfig.kitchen || currentConfig.kitchenConfig) {
        designData.kitchen = currentConfig.kitchen || currentConfig.kitchenConfig;
      }
      if (currentConfig.exterior || currentConfig.windowConfig || currentConfig.exteriorGlassOption) {
        designData.exterior = currentConfig.exterior || currentConfig.windowConfig || currentConfig.exteriorGlassOption;
      }
      if (currentConfig.roof || currentConfig.roofType || currentConfig.roofStyle) {
        designData.roof = currentConfig.roof || currentConfig.roofType || currentConfig.roofStyle;
      }
      if (currentConfig.smallHomeKitchen) designData.smallHomeKitchen = currentConfig.smallHomeKitchen;
    }

    // ============================================================================
    // SUMMIT-SPECIFIC FIELDS
    // ============================================================================
    if (modelSlug.startsWith('summit')) {
      // Summit NX Code (e.g., "A-1-a")
      if (currentConfig.summitNxCode) {
        designData.summitCode = currentConfig.summitNxCode;
      }
      
      // Summit options (derive from configuration selections)
      // These map to entryLocation, windowOptions, sideOptions etc.
      if (currentConfig.entryLocation || currentConfig.layout) {
        designData.summitOption1 = currentConfig.entryLocation || currentConfig.layout;
      }
      if (currentConfig.windowOptions || currentConfig.exterior || currentConfig.windowConfig) {
        designData.summitOption2 = currentConfig.windowOptions || currentConfig.exterior || currentConfig.windowConfig;
      }
      if (currentConfig.sideOptions) {
        designData.summitOption3 = currentConfig.sideOptions;
      }
    }

    // ============================================================================
    // SIGNATURE/PORTLAND-SPECIFIC FIELDS
    // ============================================================================
    if (modelSlug.includes('signature') || modelSlug.includes('portland')) {
      // Panel elevations (these store the elevation code like "PE12-4-W3C-D72C")
      if (currentConfig.frontPanel) designData.frontElevation = currentConfig.frontPanel;
      if (currentConfig.backPanel) designData.backElevation = currentConfig.backPanel;
      if (currentConfig.leftPanel) designData.leftElevation = currentConfig.leftPanel;
      if (currentConfig.rightPanel) designData.rightElevation = currentConfig.rightPanel;
      
      // Exterior addon (metal wainscot)
      if (currentConfig.exteriorAddon) {
        designData.exteriorAddon = currentConfig.exteriorAddon === 'metal-wainscot' ? 'metal' : 'none';
      }
      
      // Roof addon (shortened back roof - B6)
      if (currentConfig.roofAddon) {
        const roofAddonVal = String(currentConfig.roofAddon).toLowerCase();
        designData.roofAddon = roofAddonVal.includes('b6') || roofAddonVal.includes('shortened') ? 'b6' : 'none';
      }
      
      // Roof style
      if (currentConfig.roofStyle || currentConfig.roof || currentConfig.roofType) {
        designData.roofStyle = currentConfig.roofStyle || currentConfig.roof || currentConfig.roofType;
      }
    }

    // ============================================================================
    // CARRIAGE HOUSE FIELDS
    // ============================================================================
    if (modelSlug.includes('carriage')) {
      if (currentConfig.roofStyle) designData.roofStyle = currentConfig.roofStyle;
      // Map garageAccess config key to garageDoor API field, default to back-door
      designData.garageDoor = currentConfig.garageAccess
        ? String(currentConfig.garageAccess).trim().toLowerCase().replace(/\s+/g, '-')
        : 'back-door';
      // Default stairOption — carriage house only has interior stairs currently
      designData.stairOption = 'interior-stair';
      // Compute useCase for installation pricing and summary image rendering
      designData.useCase = 'carriage-house-' + (designData.roofStyle || 'gable');
    }
    
    // Add roofStyle for other models that support it (fallback)
    if (currentConfig.roofStyle && !designData.roofStyle) {
      designData.roofStyle = currentConfig.roofStyle;
    }

    // Remove undefined values
    Object.keys(designData).forEach(key => {
      if (designData[key] === undefined) delete designData[key];
    });

    return designData;
  };

  /**
   * Build labelData payload for Save Design API
   */
  const buildLabelData = (configurator) => {
    const modelSlug = configurator.getModelSlug?.() || configurator.modelConfig?.name || 'summit';
    const currentConfig = configurator.currentConfig || {};
    const modelConfig = configurator.modelConfig || {};
    
    const labelData = {
      model: kebabToTitleCase(modelSlug.split('-')[0]) // 'summit' -> 'Summit'
    };

    // Add Summit-specific labels
    if (modelSlug.startsWith('summit')) {
      if (currentConfig.siding) {
        const step = modelConfig.steps?.find(s => s.options?.siding);
        const option = step?.options?.siding?.choices?.find(c => c.value === currentConfig.siding);
        labelData.siding = option?.displayName || kebabToTitleCase(currentConfig.siding);
      }
      if (currentConfig.sidingColor) {
        const step = modelConfig.steps?.find(s => s.options?.sidingColor);
        const option = step?.options?.sidingColor?.choices?.find(c => c.value === currentConfig.sidingColor);
        labelData.sidingColor = option?.displayName || kebabToTitleCase(currentConfig.sidingColor);
      }
      if (currentConfig.summitNxCode) {
        labelData.summitNxCode = currentConfig.summitNxCode;
      }
      if (currentConfig.interiorOptions || currentConfig.interiorPackage) {
        const interiorValue = currentConfig.interiorOptions || currentConfig.interiorPackage;
        const step = modelConfig.steps?.find(s => s.options?.interiorOptions);
        const option = step?.options?.interiorOptions?.choices?.find(c => c.value === interiorValue);
        labelData.interiorKit = option?.displayName || kebabToTitleCase(interiorValue);
      }
    }

    return labelData;
  };

  /**
   * Build costData payload for Save Design API
   */
  /**
   * Resolve the product price from the pricing matrix if available.
   * Falls back to basePrice if no matrix or no match found.
   */
  const resolveMatrixPrice = (configurator) => {
    const pricing = configurator.modelConfig?.pricing || configurator.pricingConfig;
    const currentConfig = configurator.currentConfig || {};
    if (!pricing?.matrix) return null;

    const availableKeys = Object.keys(pricing.matrix);
    if (availableKeys.length === 0) return null;

    // Collect all current config values for matching
    const configValues = new Set();
    for (const key of Object.keys(currentConfig)) {
      const val = currentConfig[key];
      if (typeof val === 'string' && val) configValues.add(val);
    }

    // Try to find a matrix key where all pipe-separated parts match config values
    let matrixEntry = null;
    for (const key of availableKeys) {
      const parts = key.split('|');
      if (parts.every(p => configValues.has(p))) {
        matrixEntry = pricing.matrix[key];
        break;
      }
    }

    if (!matrixEntry) return null;

    // Resolve interior option price from the matched entry
    const interior = currentConfig.interiorOptions || currentConfig.interiorPackage || 'shell-only';
    // Try exact match, then common aliases
    return matrixEntry[interior] ?? matrixEntry['shell-only'] ?? null;
  };

  const buildCostData = (configurator) => {
    const basePrice = normalizeNumber(configurator.basePrice || 0, 0);
    const configPrice = normalizeNumber(configurator.configPrice || 0, 0);
    const finalPriceData = configurator.finalPriceData || {};
    const currentConfig = configurator.currentConfig || {};

    // Use matrix-resolved price if available, otherwise fall back to basePrice
    const matrixPrice = resolveMatrixPrice(configurator);
    const productPrice = matrixPrice !== null ? matrixPrice : basePrice + configPrice;

    const costData = {
      total: productPrice,
      shell: matrixPrice !== null ? matrixPrice : basePrice,
    };

    // Add interior kit cost
    const interiorOption = currentConfig.interiorOptions || currentConfig.interiorPackage;
    if (interiorOption && interiorOption !== 'shell-only') {
      const modelConfig = configurator.modelConfig || {};
      const step = modelConfig.steps?.find(s => s.options?.interiorOptions);
      const option = step?.options?.interiorOptions?.choices?.find(c => c.value === interiorOption);
      if (option?.price) {
        costData.interiorKit = normalizeNumber(option.price, 0);
      }
    }

    // Add installation costs
    const installationPrices = finalPriceData.breakdown?.installationPrices || {};
    if (installationPrices.shell) costData.shellInstall = normalizeNumber(installationPrices.shell, 0);
    if (installationPrices.lifestyle) costData.lifestyleInstall = normalizeNumber(installationPrices.lifestyle, 0);
    if (installationPrices.kitchenbath) costData.lifestylePlusInstall = normalizeNumber(installationPrices.kitchenbath, 0);
    
    const selectedInstallation = currentConfig.installation || 'shell';
    const installationCost = computeInstallationCost(
      finalPriceData.breakdown?.installation,
      selectedInstallation
    ) || 0;
    if (installationCost > 0) {
      costData.installationSelection = normalizeNumber(installationCost, 0);
    }

    // Add shipping
    if (finalPriceData.shipping_cost) {
      costData.shipping = normalizeNumber(finalPriceData.shipping_cost, 0);
    }

    // Add permit plan
    if (configurator.permitPlanSelected === 'include' && configurator.permitPlanPrice) {
      costData.permitPlanSetSelection = normalizeNumber(configurator.permitPlanPrice, 0);
    }

    // Add other costs from config
    const addCostFromConfig = (key, costKey) => {
      const step = configurator.modelConfig?.steps?.find(s => s.options?.[key]);
      const option = step?.options?.[key]?.choices?.find(c => c.value === currentConfig[key]);
      if (option?.price) {
        costData[costKey] = normalizeNumber(option.price, 0);
      }
    };

    addCostFromConfig('soffitColor', 'soffitColor');
    addCostFromConfig('roofAddon', 'roofAddon');
    addCostFromConfig('exteriorAddon', 'exteriorAddon');
    addCostFromConfig('siding', 'siding');
    addCostFromConfig('sidingColor', 'sidingColor');
    addCostFromConfig('trimColor', 'trimColor');
    addCostFromConfig('doorColor', 'doorColor');
    addCostFromConfig('roofStyle', 'roofStyle');

    // Remove zero/undefined values (optional - API may accept them)
    Object.keys(costData).forEach(key => {
      if (costData[key] === undefined || costData[key] === null) delete costData[key];
    });

    return costData;
  };

  /**
   * Call Save Design API
   * @param {Object} configurator - The configurator instance
   * @param {Object} formData - Form data with firstName, lastName, email, zipCode, phone
   * @param {Object} options - Optional: screenshots (exteriorScreenshotData, interiorScreenshotData, summaryScreenshotData)
   * @returns {Promise<Object>} API response
   */
  const saveDesignToAPI = async (configurator, formData, options = {}) => {
    const API_TOKEN = window.STUDIO_SHED_DESIGN_API_TOKEN || ''; // Should be set in theme settings
    const API_ENDPOINT = window.STUDIO_SHED_DESIGN_API_ENDPOINT || 'https://design.studio-shed.com/api/designs';
    const requestId = options.requestId || `api_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Log entry point with stack trace to identify all callers
    console.log('[Save Design API] FUNCTION CALLED', {
      requestId,
      timestamp: new Date().toISOString(),
      endpoint: API_ENDPOINT,
      email: formData?.email,
      zipCode: formData?.zipCode,
      stackTrace: new Error().stack
    });
    
    // Check for duplicate calls using a global map
    if (!window._saveDesignAPICalls) {
      window._saveDesignAPICalls = new Map();
    }
    
    // Check if this exact request is already in progress (same email + zip within 5 seconds)
    const requestKey = `${formData?.email}_${formData?.zipCode}`;
    const existingCall = window._saveDesignAPICalls.get(requestKey);
    const now = Date.now();
    
    if (existingCall && (now - existingCall.timestamp) < 5000) {
      console.warn('[Save Design API] DUPLICATE CALL DETECTED - returning cached promise', {
        requestId,
        existingRequestId: existingCall.requestId,
        timestamp: new Date().toISOString(),
        timeSinceLastCall: now - existingCall.timestamp
      });
      // Return the existing promise instead of blocking
      return existingCall.promise;
    }
    
    // Clean up old entries (older than 10 seconds)
    for (const [key, value] of window._saveDesignAPICalls.entries()) {
      if (now - value.timestamp > 10000) {
        window._saveDesignAPICalls.delete(key);
      }
    }
    
    // Debug logging
    console.log('[Save Design API] Checking configuration...', {
      hasToken: !!API_TOKEN,
      tokenLength: API_TOKEN ? API_TOKEN.length : 0,
      endpoint: API_ENDPOINT
    });
    
    if (!API_TOKEN) {
      console.warn('[Save Design API] API token not configured. Set window.STUDIO_SHED_DESIGN_API_TOKEN to enable design saving.');
      return { success: false, error: 'API token not configured' };
    }

    const payload = {
      customerData: buildCustomerData(formData),
      designData: buildDesignData(configurator),
      labelData: buildLabelData(configurator),
      costData: buildCostData(configurator),
    };

    // Add optional screenshots
    if (options.exteriorScreenshotData) payload.exteriorScreenshotData = options.exteriorScreenshotData;
    if (options.interiorScreenshotData) payload.interiorScreenshotData = options.interiorScreenshotData;
    if (options.summaryScreenshotData) payload.summaryScreenshotData = options.summaryScreenshotData;

    console.log('[Save Design API] Making request to:', API_ENDPOINT, {
      requestId,
      timestamp: new Date().toISOString()
    });
    console.log('[Save Design API] Payload keys:', Object.keys(payload));

    // Create the API promise and store it to prevent duplicate calls
    const apiPromise = (async () => {
      try {
        const response = await fetch(API_ENDPOINT, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${API_TOKEN}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        console.log('[Save Design API] Response status:', response.status, response.statusText);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('[Save Design API] Error response:', errorData);
          throw new Error(errorData.message || `API request failed with status ${response.status}`);
        }

        const result = await response.json();
        console.log('[Save Design API] Success:', {
          requestId,
          ...result
        });

        // Store visitor identity for HubSpot chat widget identification
        if (window.VisitorIdentity && formData.email) {
          window.VisitorIdentity.store(formData.email, formData.firstName, formData.lastName);
        }

        return { success: true, ...result };
      } catch (error) {
        // Enhanced error logging for CORS/network issues
        if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
          console.error('[Save Design API] Network/CORS error. Check:', {
            endpoint: API_ENDPOINT,
            hasToken: !!API_TOKEN,
            tokenLength: API_TOKEN ? API_TOKEN.length : 0,
            error: error.message,
            suggestion: 'This might be a CORS issue. Verify the endpoint allows requests from your domain.'
          });
        } else {
          console.error('[Save Design API] Request failed:', {
            requestId,
            error: error.message
          });
        }
        
        throw error;
      } finally {
        // Remove from tracking map when done (success or error)
        window._saveDesignAPICalls.delete(requestKey);
      }
    })();

    // Store the promise immediately to prevent duplicate calls
    window._saveDesignAPICalls.set(requestKey, { requestId, timestamp: now, promise: apiPromise });

    return apiPromise;
  };

  // ============================================================================
  // PERMIT MANAGER - Shared permit system for all configurators
  // ============================================================================

  /**
   * PermitManager - Centralized permit plan handling for all configurator modals.
   * Eliminates duplicate permit code across carriage, portland, signature, aspect, and summit modals.
   * 
   * @param {Object} config - Configuration object
   * @param {Object} config.configurator - Parent configurator instance (required)
   * @param {string} config.drawerId - DOM ID of the permit drawer element (required)
   * @param {number} [config.basePrice=6995] - Base permit price in dollars (Signature: 5995, others: 6995)
   * @param {Function} [config.formatPrice] - Price formatter function (defaults to formatPriceSmart)
   * @param {Function} [config.onSelect] - Callback after permit selection (e.g., recalculate totals)
   * @param {HTMLElement} [config.step2Section] - Step 2 section element for DOM queries
   */
  class PermitManager {
    constructor(config) {
      if (!config?.configurator) {
        console.error('[PermitManager] Missing required configurator instance');
        return;
      }
      if (!config?.drawerId) {
        console.error('[PermitManager] Missing required drawerId');
        return;
      }

      this.configurator = config.configurator;
      this.drawerId = config.drawerId;
      this.basePrice = normalizeNumber(config.basePrice, 6995);
      this.formatPrice = config.formatPrice || formatPriceSmart;
      this.onSelect = config.onSelect || null;
      this.step2Section = config.step2Section || null;

      // State - also synced to configurator for backward compatibility
      this.permitDrawer = null;
      this.defaultSelected = config.defaultSelected
        ?? config.configurator?.permitPlanSelected
        ?? 'exclude';
      this.selected = this.defaultSelected;  // 'exclude', 'none', or 'include'
      this.price = this.selected === 'include' ? this.basePrice : 0;
      
      console.log(`[Permit Constructor] Set! Argument inside config: ${config.defaultSelected}. Final resolved default: ${this.defaultSelected}`);

      // Sync initial state to configurator
      this._syncToConfigurator();
    }

    /**
     * Sync permit state to the parent configurator for backward compatibility
     * with existing price calculation code.
     */
    _syncToConfigurator() {
      if (this.configurator) {
        this.configurator.permitPlanSelected = this.selected;
        this.configurator.permitPlanPrice = this.price;
        this.configurator.permitPlanBasePrice = this.basePrice;
      }
    }

    /**
     * Get the drawer element, caching the reference.
     * @returns {HTMLElement|null}
     */
    _getDrawer() {
      if (!this.permitDrawer) {
        this.permitDrawer = document.getElementById(this.drawerId);
      }
      return this.permitDrawer;
    }

    /**
     * Initialize the permit drawer - attach event listeners and sync initial state.
     * Call this once after the drawer HTML is in the DOM.
     */
    setup() {
      const drawer = this._getDrawer();
      if (!drawer) {
        console.warn(`[PermitManager] Drawer not found: #${this.drawerId}`);
        return;
      }

      // Ensure the include option reflects the current base permit price
      const includeOption = drawer.querySelector('[data-permit-option][data-permit-value="include"]');
      if (includeOption) {
        includeOption.dataset.permitPrice = String(this.basePrice);
      }

      // Update price text in drawer if element exists
      const includePriceText = drawer.querySelector('[data-permit-drawer-price]');
      if (includePriceText) {
        includePriceText.textContent = this.basePrice > 0 ? this.formatPrice(this.basePrice) : 'No charge';
      }

      // Attach click listeners to permit options
      drawer.querySelectorAll('[data-permit-option]').forEach(card => {
        card.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.handleSelect(card);
        });
      });

      // Set initial selection state
      this.updateSelection();
      this.updateSummaryDisplay();
    }

    /**
     * Open the permit drawer with current selection state.
     */
    open() {
      const drawer = this._getDrawer();
      if (!drawer) {
        console.warn(`[PermitManager] Cannot open - drawer not found: #${this.drawerId}`);
        return;
      }

      this.updateSelection();

      // Try native drawer method first, fallback to class toggle
      if (typeof drawer.openDrawer === 'function') {
        drawer.openDrawer();
      } else {
        drawer.classList.add('open');
        drawer.setAttribute('aria-hidden', 'false');
        if (typeof SiteOverlay !== 'undefined' && SiteOverlay.showOverlay) {
          SiteOverlay.showOverlay();
        }
      }
    }

    /**
     * Sync the drawer UI to reflect the current selection state.
     */
    updateSelection() {
      const drawer = this._getDrawer();
      if (!drawer) return;

      drawer.querySelectorAll('[data-permit-option]').forEach(card => {
        const isSelected = card.dataset.permitValue === this.selected;
        card.classList.toggle('selected', isSelected);
        card.classList.toggle('config-card--selected', isSelected);
      });
    }

    /**
     * Handle user selection of a permit option.
     * @param {HTMLElement} card - The clicked option card
     */
    handleSelect(card) {
      const value = card.dataset.permitValue;
      const price = parseInt(card.dataset.permitPrice, 10) || 0;

      // Update state
      this.selected = value;
      this.price = price;
      this._syncToConfigurator();

      // Update drawer UI
      const drawer = this._getDrawer();
      if (drawer) {
        drawer.querySelectorAll('[data-permit-option]').forEach(c => {
          c.classList.remove('selected', 'config-card--selected');
        });
        card.classList.add('selected', 'config-card--selected');
      }

      // Update summary display
      this.updateSummaryDisplay();

      // Call onSelect callback (e.g., to recalculate total)
      if (typeof this.onSelect === 'function') {
        this.onSelect();
      }
    }

    /**
     * Update the permit display in the summary section.
     * @param {HTMLElement} [container] - Optional container element, defaults to step2Section's panel wrapper
     */
    updateSummaryDisplay(container) {
      const contentWrapper = container || 
        (this.step2Section ? this.step2Section.querySelector('[data-panel-content-wrapper]') : null) ||
        (this.configurator?.step2Section ? this.configurator.step2Section.querySelector('[data-panel-content-wrapper]') : null);
      
      if (!contentWrapper) return;

      // Update price display
      const priceDisplay = contentWrapper.querySelector('[data-permit-price-display]');
      if (priceDisplay) {
        priceDisplay.textContent = this.price > 0 ? this.formatPrice(this.price) : 'No charge';
      }

      // Update permit line item label      
      const permitLineItem = contentWrapper.querySelector('[data-summary-line-item="permit"], [data-item-id="permit"]');
      if (permitLineItem) {
        const label = permitLineItem.querySelector('[data-line-item-label], .line-item-label');
        if (label) {
          label.textContent = this.selected === 'include' ? 'Include Permit Plans' : 'No Permit Plans';
        }
      }
    }

    /**
     * Get the current permit state.
     * @returns {{ selected: string, price: number, basePrice: number }}
     */
    getState() {
      return {
        selected: this.selected,
        price: this.price,
        basePrice: this.basePrice
      };
    }

    /**
     * Reset permit to default state.
     */
    reset() {
      this.selected = this.defaultSelected;
      this.price = this.selected === 'include' ? this.basePrice : 0;
      this._syncToConfigurator();
      this.updateSelection();
      this.updateSummaryDisplay();
    }

    /**
     * Set permit state programmatically.
     * @param {{ selected?: string, price?: number }} state
     */
    setState(state) {
      if (state.selected !== undefined) {
        this.selected = state.selected;
      }
      if (state.price !== undefined) {
        this.price = normalizeNumber(state.price, 0);
      }
      this._syncToConfigurator();
      this.updateSelection();
    }
  }

  // ============================================================================
  // FOUNDATION MANAGER - Shared foundation type drawer logic
  // ============================================================================

  /**
   * FoundationManager centralizes foundation type drawer logic across all configurator models.
   * Similar to PermitManager, it provides a single source of truth for:
   * - Drawer setup and event handling
   * - Selection state management
   * - Summary display updates
   * - Backward compatibility with existing configurator properties
   */
  class FoundationManager {
    constructor(config) {
      if (!config?.configurator) {
        console.error('[FoundationManager] Missing required configurator instance');
        return;
      }

      this.configurator = config.configurator;
      this.drawerId = config.drawerId || 'foundation-type-drawer';
      this.defaultValue = config.defaultValue || 'wood-frame';
      this.step2Section = config.step2Section || null;
      this.onSelect = config.onSelect || null;

      // State - synced to configurator for backward compatibility
      this.foundationDrawer = null;
      this.selected = this.defaultValue;

      // Labels for summary display (can be customized per model)
      this.labels = config.labels || {
        'concrete-slab': 'Foundation: Concrete Slab',
        'wood-frame': 'Foundation: Wood Frame Floor',
        'not-sure': 'Foundation TBD'
      };

      // Sync initial state to configurator
      this._syncToConfigurator();
    }

    /**
     * Sync foundation state to the parent configurator for backward compatibility
     * with existing code that reads foundationTypeSelected directly.
     */
    _syncToConfigurator() {
      if (this.configurator) {
        this.configurator.foundationTypeSelected = this.selected;
      }
    }

    /**
     * Get the drawer element, caching the reference.
     * @returns {HTMLElement|null}
     */
    _getDrawer() {
      if (!this.foundationDrawer) {
        this.foundationDrawer = document.getElementById(this.drawerId);
      }
      return this.foundationDrawer;
    }

    /**
     * Initialize the foundation drawer - attach event listeners and sync initial state.
     * Call this once after the drawer HTML is in the DOM.
     */
    setup() {
      const drawer = this._getDrawer();
      if (!drawer) {
        console.warn(`[FoundationManager] Drawer not found: #${this.drawerId}`);
        return;
      }

      // Attach click listeners to foundation options
      drawer.querySelectorAll('[data-foundation-option]').forEach(card => {
        card.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.handleSelect(card);
        });
      });

      // Set initial selection state
      this.updateSelection();
    }

    /**
     * Open the foundation drawer with current selection state.
     */
    open() {
      const drawer = this._getDrawer();
      if (!drawer) {
        console.warn(`[FoundationManager] Cannot open - drawer not found: #${this.drawerId}`);
        return;
      }

      this.updateSelection();

      // Try native drawer method first, fallback to class toggle
      if (typeof drawer.openDrawer === 'function') {
        drawer.openDrawer();
      } else {
        drawer.classList.add('open');
        drawer.setAttribute('aria-hidden', 'false');
        if (typeof SiteOverlay !== 'undefined' && SiteOverlay.showOverlay) {
          SiteOverlay.showOverlay();
        }
      }
    }

    /**
     * Sync the drawer UI to reflect the current selection state.
     */
    updateSelection() {
      const drawer = this._getDrawer();
      if (!drawer) return;

      drawer.querySelectorAll('[data-foundation-option]').forEach(card => {
        const isSelected = card.dataset.foundationValue === this.selected;
        card.classList.toggle('selected', isSelected);
        card.classList.toggle('config-card--selected', isSelected);
      });
    }

    /**
     * Handle user selection of a foundation option.
     * @param {HTMLElement} card - The clicked option card
     */
    handleSelect(card) {
      const value = card.dataset.foundationValue;

      // Update state
      this.selected = value;
      this._syncToConfigurator();

      // Update drawer UI
      const drawer = this._getDrawer();
      if (drawer) {
        drawer.querySelectorAll('[data-foundation-option]').forEach(c => {
          c.classList.remove('selected', 'config-card--selected');
        });
        card.classList.add('selected', 'config-card--selected');
      }

      // Update summary display
      this.updateSummaryDisplay();

      // Call onSelect callback if provided
      if (typeof this.onSelect === 'function') {
        this.onSelect();
      }
    }

    /**
     * Update the foundation display in the summary section.
     */
    updateSummaryDisplay() {
      const contentWrapper = 
        (this.step2Section ? this.step2Section.querySelector('[data-panel-content-wrapper]') : null) ||
        (this.configurator?.step2Section ? this.configurator.step2Section.querySelector('[data-panel-content-wrapper]') : null);
      
      if (!contentWrapper) return;

      const foundationLabel = contentWrapper.querySelector('[data-foundation-label]');
      if (foundationLabel) {
        foundationLabel.textContent = this.labels[this.selected] || 'Foundation TBD';
      }
    }

    /**
     * Get the current foundation state.
     * @returns {{ selected: string }}
     */
    getState() {
      return {
        selected: this.selected
      };
    }

    /**
     * Reset foundation to default state.
     */
    reset() {
      this.selected = this.defaultValue;
      this._syncToConfigurator();
      this.updateSelection();
    }

    /**
     * Set foundation state programmatically.
     * @param {{ selected?: string }} state
     */
    setState(state) {
      if (state.selected !== undefined) {
        this.selected = state.selected;
      }
      this._syncToConfigurator();
      this.updateSelection();
    }
  }

  // ============================================================================
  // FORM VALIDATION
  // ============================================================================

  /**
   * Default validation rules for configurator forms.
   * Can be extended or overridden when creating a validator.
   */
  const DEFAULT_FORM_VALIDATION_RULES = {
    firstName: {
      required: true,
      minLength: 2,
      pattern: /^[a-zA-Z\s'\-]+$/,
      messages: {
        required: 'First name is required',
        minLength: 'First name must be at least 2 characters',
        pattern: 'First name can only contain letters, spaces, hyphens, and apostrophes'
      }
    },
    lastName: {
      required: true,
      minLength: 2,
      pattern: /^[a-zA-Z\s'\-]+$/,
      messages: {
        required: 'Last name is required',
        minLength: 'Last name must be at least 2 characters',
        pattern: 'Last name can only contain letters, spaces, hyphens, and apostrophes'
      }
    },
    email: {
      required: true,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      messages: {
        required: 'Email is required',
        pattern: 'Please enter a valid email address'
      }
    },
    zipCode: {
      required: true,
      pattern: /^\d{5}(-\d{4})?$/,
      messages: {
        required: 'ZIP code is required',
        pattern: 'Please enter a valid 5-digit ZIP code'
      }
    },
    phone: {
      required: false,
      pattern: /^[\d\s\-\(\)\+]*$/,
      messages: {
        pattern: 'Please enter a valid phone number'
      }
    }
  };

  /**
   * Creates a reusable form validator for configurator forms.
   * Provides real-time validation, error display, and form submission validation.
   * 
   * @param {HTMLFormElement} form - The form element to validate
   * @param {Object} options - Validation options
   * @param {Object} options.rules - Custom validation rules to merge with defaults
   * @param {boolean} options.validateOnBlur - Whether to validate on blur (default: true)
   * @param {boolean} options.validateOnInput - Whether to clear errors on input (default: true)
   * @returns {Object} - Validator instance with methods
   * 
   * @example
   * const validator = createFormValidator(form);
   * validator.attachListeners();
   * 
   * form.addEventListener('submit', (e) => {
   *   e.preventDefault();
   *   const { valid, firstInvalid } = validator.validateAll();
   *   if (!valid) {
   *     firstInvalid?.focus();
   *     return;
   *   }
   *   // proceed with submission
   * });
   */
  const createFormValidator = (form, options = {}) => {
    if (!form || !(form instanceof HTMLFormElement)) {
      console.warn('[FormValidator] Invalid form element provided');
      return null;
    }

    const rules = { ...DEFAULT_FORM_VALIDATION_RULES, ...(options.rules || {}) };
    const validateOnBlur = options.validateOnBlur !== false;
    const validateOnInput = options.validateOnInput !== false;

    /**
     * Validate a single input field against its rules.
     * @param {HTMLInputElement} input - The input element to validate
     * @returns {{ valid: boolean, message?: string }}
     */
    const validateField = (input) => {
      const name = input.name;
      const value = input.value.trim();
      const rule = rules[name];

      // No rule defined for this field - consider it valid
      if (!rule) return { valid: true };

      // Required check
      if (rule.required && !value) {
        return { valid: false, message: rule.messages?.required || `${name} is required` };
      }

      // Skip other validations if empty and not required
      if (!value && !rule.required) return { valid: true };

      // Min length check
      if (rule.minLength && value.length < rule.minLength) {
        return { 
          valid: false, 
          message: rule.messages?.minLength || `Minimum ${rule.minLength} characters required` 
        };
      }

      // Max length check
      if (rule.maxLength && value.length > rule.maxLength) {
        return { 
          valid: false, 
          message: rule.messages?.maxLength || `Maximum ${rule.maxLength} characters allowed` 
        };
      }

      // Pattern check
      if (rule.pattern && !rule.pattern.test(value)) {
        return { valid: false, message: rule.messages?.pattern || 'Invalid format' };
      }

      // Custom validator function
      if (typeof rule.validate === 'function') {
        const customResult = rule.validate(value, input);
        if (customResult !== true) {
          return { 
            valid: false, 
            message: typeof customResult === 'string' ? customResult : (rule.messages?.custom || 'Invalid value') 
          };
        }
      }

      return { valid: true };
    };

    /**
     * Show error state on a field with visual feedback.
     * @param {HTMLInputElement} input - The input element
     * @param {string} message - Error message to display
     */
    const showFieldError = (input, message) => {
      input.classList.add('is-invalid');
      input.classList.remove('is-valid');
      input.setCustomValidity(message);

      // Add/update error message element
      const inputGroup = input.closest('.input-group') || input.parentElement;
      let errorEl = inputGroup.querySelector('.invalid-feedback');
      if (!errorEl) {
        errorEl = document.createElement('div');
        errorEl.className = 'invalid-feedback';
        inputGroup.appendChild(errorEl);
      }
      errorEl.textContent = message;
      errorEl.style.display = 'block';
    };

    /**
     * Clear error state on a field.
     * @param {HTMLInputElement} input - The input element
     * @param {boolean} showValid - Whether to show valid state (default: true)
     */
    const clearFieldError = (input, showValid = true) => {
      input.classList.remove('is-invalid');
      if (showValid && input.value.trim()) {
        input.classList.add('is-valid');
      } else {
        input.classList.remove('is-valid');
      }
      input.setCustomValidity('');

      const inputGroup = input.closest('.input-group') || input.parentElement;
      const errorEl = inputGroup.querySelector('.invalid-feedback');
      if (errorEl) {
        errorEl.textContent = '';
        errorEl.style.display = 'none';
      }
    };

    /**
     * Validate all form fields and return the result.
     * @returns {{ valid: boolean, firstInvalid: HTMLInputElement|null, errors: Object }}
     */
    const validateAll = () => {
      const inputs = form.querySelectorAll('input');
      let isValid = true;
      let firstInvalid = null;
      const errors = {};

      inputs.forEach(input => {
        const result = validateField(input);
        if (!result.valid) {
          showFieldError(input, result.message);
          errors[input.name] = result.message;
          isValid = false;
          if (!firstInvalid) firstInvalid = input;
        } else {
          clearFieldError(input);
        }
      });

      return { valid: isValid, firstInvalid, errors };
    };

    /**
     * Reset all validation states on the form.
     */
    const resetValidation = () => {
      const inputs = form.querySelectorAll('input');
      inputs.forEach(input => {
        input.classList.remove('is-invalid', 'is-valid');
        input.setCustomValidity('');
        const inputGroup = input.closest('.input-group') || input.parentElement;
        const errorEl = inputGroup.querySelector('.invalid-feedback');
        if (errorEl) {
          errorEl.textContent = '';
          errorEl.style.display = 'none';
        }
      });
    };

    /**
     * Attach real-time validation event listeners to form inputs.
     * Call this after the form is added to the DOM.
     */
    const attachListeners = () => {
      const inputs = form.querySelectorAll('input');
      
      inputs.forEach(input => {
        // Skip if listeners already attached
        if (input.dataset.validatorAttached === 'true') return;
        input.dataset.validatorAttached = 'true';

        // Validate on blur (when user leaves field)
        if (validateOnBlur) {
          input.addEventListener('blur', () => {
            const result = validateField(input);
            if (!result.valid) {
              showFieldError(input, result.message);
            } else {
              clearFieldError(input);
            }
          });
        }

        // Clear error on input (user is fixing the error)
        if (validateOnInput) {
          input.addEventListener('input', () => {
            // Only re-validate if field was previously invalid
            if (input.classList.contains('is-invalid')) {
              const result = validateField(input);
              if (result.valid) {
                clearFieldError(input);
              }
            }
          });
        }
      });
    };

    /**
     * Check if the form is currently valid without showing errors.
     * @returns {boolean}
     */
    const isValid = () => {
      const inputs = form.querySelectorAll('input');
      for (const input of inputs) {
        const result = validateField(input);
        if (!result.valid) return false;
      }
      return true;
    };

    return {
      validateField,
      validateAll,
      showFieldError,
      clearFieldError,
      resetValidation,
      attachListeners,
      isValid,
      rules
    };
  };

  // ============================================================================
  // SUMMIT NX CODE RESOLVER
  // ============================================================================

  /**
   * Complete mapping of Summit model options to NX component codes.
   * Structure varies by model complexity:
   *   - 1-level:  { 'option-value': 'CODE' }
   *   - 2-level:  { 'opt1-value': { 'opt2-value': 'CODE' } }
   *   - 3-level:  { 'opt1-value': { 'opt2-value': { 'opt3-value': 'CODE' } } }
   */

  const SUMMIT_NX_OPTIONS = {
    308: {
      a: {
        'front-entry': { 'more-glass': 'A-1-a', 'more-privacy': 'A-1-b' },
        'side-entry':  { 'more-glass': 'A-2-a', 'more-privacy': 'A-2-b' }
      },
      b: {
        'front-entry': { 'more-glass': 'B-1-a', 'more-privacy': 'B-1-b' },
        'side-entry':  { 'more-glass': 'B-2-a', 'more-privacy': 'B-2-b' }
      }
    },
    440: {
      a: {
        'more-operable': { 'side-window': 'A-1-a', 'side-door': 'A-1-b' },
        'more-glass':    { 'side-window': 'A-2-a', 'side-door': 'A-2-b' }
      },
      b: {
        'more-operable': { 'side-window': 'B-1-a', 'side-door': 'B-1-b' },
        'more-glass':    { 'side-window': 'B-2-a', 'side-door': 'B-2-b' }
      }
    },
    476: {
      a: { 'side-window': 'A-1', 'side-door': 'A-2' },
      b: { 'side-window': 'B-1', 'side-door': 'B-2' }
    },
    608: {
      a: { 'side-window': 'A-1', 'side-door': 'A-2' },
      b: { 'side-window': 'B-1', 'side-door': 'B-2' }
    },
    680: {
      a: {
        'front-door': { 'bedroom-window': 'A-1-a', 'bedroom-door': 'A-1-b' },
        'side-door':  { 'front-windows': 'A-2-a', 'front-slider-doors': 'A-2-b' }
      },
      b: {
        'front-door': { 'bedroom-window': 'B-1-a', 'bedroom-door': 'B-1-b' },
        'back-door':  { 'front-windows': 'B-2-a', 'front-slider-doors': 'B-2-b' }
      },
      c: {
        'front-door': { 'bedroom-window': 'C-1-a', 'bedroom-door': 'C-1-b' },
        'side-door':  { 'front-windows': 'C-2-a', 'front-slider-doors': 'C-2-b' }
      }
    },
    800: {
      a: {
        'standard': {
          'front-door': { 'side-window': 'A-1-a-i', 'side-slider-door': 'A-1-a-ii' },
          'side-door':  { 'front-window': 'A-1-b-i', 'front-slider-door': 'A-1-b-ii' }
        },
        'add-half-bath': {
          'front-door': { 'side-window': 'A-2-a-i', 'side-slider-door': 'A-2-a-ii' },
          'side-door':  { 'front-window': 'A-2-b-i', 'front-slider-door': 'A-2-b-ii' }
        }
      },
      b: {
        'single-bathroom': { 'bedroom-window': 'B-1-a', 'bedroom-door': 'B-1-b' },
        'add-half-bath':   { 'bedroom-window': 'B-2-a', 'bedroom-door': 'B-2-b' },
        'add-full-bath':   { 'bedroom-window': 'B-3-a', 'bedroom-door': 'B-3-b' }
      }
    },
    1000: {
      a: {
        'more-storage': {
          'double-door': { 'bedroom-door': 'A-1-a-i', 'bedroom-window': 'A-1-a-ii' },
          'single-door': { 'bedroom-door': 'A-1-b-i', 'bedroom-window': 'A-1-b-ii' }
        },
        'kitchen-window': {
          'double-door': { 'bedroom-door': 'A-2-a-i', 'bedroom-window': 'A-2-a-ii' },
          'single-door': { 'bedroom-door': 'A-2-b-i', 'bedroom-window': 'A-2-b-ii' }
        },
        'kitchen-door': {
          'double-door': { 'bedroom-door': 'A-3-a-i', 'bedroom-window': 'A-3-a-ii' },
          'single-door': { 'bedroom-door': 'A-3-b-i', 'bedroom-window': 'A-3-b-ii' }
        }
      },
      b: {
        'larger-living': {
          'bedroom-window': { 'kitchen-window': 'B-1-a-i', 'kitchen-door': 'B-1-a-ii' },
          'bedroom-door':   { 'kitchen-window': 'B-1-b-i', 'kitchen-door': 'B-1-b-ii' }
        },
        'larger-kitchen': {
          'bedroom-window': { 'kitchen-window': 'B-2-a-i', 'kitchen-door': 'B-2-a-ii' },
          'bedroom-door':   { 'kitchen-window': 'B-2-b-i', 'kitchen-door': 'B-2-b-ii' }
        },
        'larger-entry': {
          'bedroom-window': { 'kitchen-window': 'B-3-a-i', 'kitchen-door': 'B-3-a-ii' },
          'bedroom-door':   { 'kitchen-window': 'B-3-b-i', 'kitchen-door': 'B-3-b-ii' }
        }
      }
    }
  };

  /**
   * Resolve the summitNxCode from the mapping table using current config values.
   *
   * @param {string} modelSlug - e.g., "summit-608-a", "summit-1000-b"
   * @param {Object} currentConfig - the configurator's currentConfig object
   * @returns {string|null} the resolved code (e.g., "A-2", "B-2-b-ii") or null if not found
   *
   * Algorithm: extracts model size (608) and variant (a) from the slug,
   * then recursively walks the nested mapping. At each level, it checks
   * every value in currentConfig against the keys of the current mapping node.
   * When it reaches a string leaf, that's the code.
   */
  const resolveSummitNxCode = (modelSlug, currentConfig) => {
    // Extract size and variant from slug: "summit-608-a" -> size=608, variant="a"
    const match = String(modelSlug).match(/summit-(\d+)-([a-z])/i);
    if (!match) return null;

    const size = parseInt(match[1], 10);
    const variant = match[2].toLowerCase();

    const sizeMap = SUMMIT_NX_OPTIONS[size];
    if (!sizeMap) return null;

    const variantMap = sizeMap[variant];
    if (!variantMap) return null;

    // Collect all current config values into a flat set for fast lookup
    const configValues = new Set();
    for (const key of Object.keys(currentConfig)) {
      const val = currentConfig[key];
      if (typeof val === 'string') configValues.add(val);
    }

    // Recursively walk the mapping tree, matching config values at each level
    const walk = (node) => {
      if (typeof node === 'string') return node; // Leaf — this is the code
      if (!node || typeof node !== 'object') return null;

      const keys = Object.keys(node);
      for (const key of keys) {
        if (configValues.has(key)) {
          const result = walk(node[key]);
          if (result) return result;
        }
      }
      return null;
    };

    return walk(variantMap);
  };

  /* ── Step History Manager ──────────────────────────────────────────────
   * Pushes a history entry when entering Step 2 so the browser back
   * button returns the user to Step 1 instead of leaving the page.
   */
  const StepHistoryManager = {
    _activeConfigurator: null,

    /** Call when entering step 2 */
    pushStep2(configuratorInstance) {
      this._activeConfigurator = configuratorInstance;
      const url = new URL(window.location.href);
      url.searchParams.set('step', '2');
      window.history.pushState({ configuratorStep: 2 }, '', url.toString());
    },

    /** Call when leaving step 2 programmatically (e.g., click title) */
    exitStep2() {
      this._activeConfigurator = null;
      window.history.back();
    },

    /** Initialize the popstate listener (call once on page load) */
    init() {
      window.addEventListener('popstate', (e) => {
        if (this._activeConfigurator && (!e.state || !e.state.configuratorStep)) {
          const configurator = this._activeConfigurator;
          this._activeConfigurator = null;
          configurator.hideStep2({ fromPopstate: true });
        }
      });
    }
  };

  StepHistoryManager.init();

  root.ConfiguratorShared = Object.assign(root.ConfiguratorShared || {}, {
    resolveSummitNxCode,
    StepHistoryManager,
    normalizeNumber,
    formatUSD,
    formatPriceSmart,
    formatMoneyFromDollars,
    slugify,
    canonicalModel,
    pickFirst,
    buildInstallationCostPayload,
    getInstallationCostKeys,
    isProductOnlyOption,
    computeInstallationCost,
    selectInstallationCost,
    createInstallationCostClient,
    applyInstallationCostsToServiceOptions,
    queueInstallationPricing,
    validateZipCodeWithAPI,
    showNotification,
    showError,
    showSuccess,
    showContactLinkOnError,
    saveDesignToAPI,
    buildCustomerData,
    buildDesignData,
    buildLabelData,
    buildCostData,
    PermitManager,
    FoundationManager,
    createFormValidator,
    DEFAULT_FORM_VALIDATION_RULES,
  });
})();


